import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { getCustomersList, getCustomerStats } from "../../lib/supabase/customers";
import NewCustomerModal from "./NewCustomerModal";
import { hasPermission } from "../../lib/permissions";

import { FiSearch, FiX, FiChevronDown, FiPlus, FiRefreshCw, FiChevronRight } from "react-icons/fi";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowDownAZ, ArrowUpAZ, Users, UserPlus, Phone } from "lucide-react";
import BackButton from "../common/BackButton";

// ─── constants ────────────────────────────────────────────────────────────────
const PAGE_LIMIT = 50;
const STORAGE_KEYS = {
    sortBy: 'customers_sortBy',
    searchQuery: 'customers_searchQuery',
    customerTypeFilter: 'customers_customerTypeFilter',
};

const CUSTOMER_TYPE_OPTIONS = [
    { value: "all", label: "All Customer Types" },
    { value: "none", label: "None" },
    { value: "Account", label: "Account" },
    { value: "COD", label: "COD" },
];

const CUSTOMER_TYPE_META = {
    Account: "bg-blue-50 text-blue-700 border-blue-200",
    COD: "bg-amber-50 text-amber-700 border-amber-200",
};

const load = (key, def) => {
    try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : def; }
    catch { return def; }
};
const save = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { }
};

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First", icon: ArrowDownNarrowWide },
    { value: "oldest", label: "Oldest First", icon: ArrowUpNarrowWide },
    { value: "alphabetical", label: "A → Z", icon: ArrowDownAZ },
    { value: "reverse_alphabetical", label: "Z → A", icon: ArrowUpAZ },
];

const formatDate = (ds) => {
    if (!ds) return "—";
    return new Date(ds).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Australian phone formatting ──────────────────────────────────────────────
const formatAustralianPhone = (raw) => {
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    const local = (digits.startsWith('61') && digits.length >= 11) ? '0' + digits.slice(2) : digits;
    if (/^04/.test(local)) {
        // Mobile: 04XX XXX XXX
        if (local.length <= 4) return local;
        if (local.length <= 7) return `${local.slice(0, 4)} ${local.slice(4)}`;
        return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 10)}`;
    }
    if (/^0[2378]/.test(local)) {
        // Landline: (0X) XXXX XXXX
        if (local.length <= 2) return local;
        if (local.length <= 6) return `(${local.slice(0, 2)}) ${local.slice(2)}`;
        return `(${local.slice(0, 2)}) ${local.slice(2, 6)} ${local.slice(6, 10)}`;
    }
    if (/^1[389]00/.test(local)) {
        // 1300 / 1800 / 1900: XXXX XXX XXX
        if (local.length <= 4) return local;
        if (local.length <= 7) return `${local.slice(0, 4)} ${local.slice(4)}`;
        return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
    }
    if (/^13\d/.test(local) && local.length <= 6) {
        // 13 XXXX
        return local.length <= 2 ? local : `${local.slice(0, 2)} ${local.slice(2)}`;
    }
    return raw; // unrecognised — return as-is
};

// ─── Grid column template (shared by header + rows) ──────────────────────────
const COL_TEMPLATE = "1fr 1fr 1fr 0.8fr 0.2fr 28px";

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => {
    const initials = (name || "?")
        .split(" ")
        .slice(0, 2)
        .map(n => n[0] || "")
        .join("")
        .toUpperCase();
    return (
        <div className="w-9 h-9 flex-shrink-0 rounded-full bg-zinc-700 text-white flex items-center justify-center text-[13px] font-semibold leading-none select-none">
            {initials}
        </div>
    );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, Icon }) => (
    <div className="customer-stat bg-white rounded-xl border border-gray-300 px-5 py-4 shadow-md flex flex-col gap-1">
        <div className="flex items-center justify-between mb-0.5">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{label}</span>
            {Icon && <Icon size={13} className="text-gray-300" />}
        </div>
        <span className="text-[28px] font-semibold text-zinc-700 leading-none">{value}</span>
        {sub && <span className="text-[11px] text-gray-400 mt-0.5">{sub}</span>}
    </div>
);

// ─── Customer row ─────────────────────────────────────────────────────────────
const CustomerRow = React.memo(({ customer, companySlug, navigate, location, measureElement, virtualIndex }) => (
    <div
        onClick={() => navigate(`/${companySlug}/customers/${customer.id}`, {
            state: { from: location.pathname }
        })}
        ref={measureElement}
        data-index={virtualIndex}
        className="customer-mobile-row grid items-center gap-4 px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors group"
        style={{ gridTemplateColumns: COL_TEMPLATE }}
    >
        {/* Customer name + email */}
        <div className="flex items-center gap-3 min-w-0">
            <Avatar name={customer.name} />
            <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-sm font-semibold text-zinc-700 leading-snug truncate">
                        {customer.name || "—"}
                    </p>

                    {customer.customer_type && (
                        <span className={`shrink-0 px-1.5 py-[1px] rounded-full border text-[9px] font-black uppercase ${CUSTOMER_TYPE_META[customer.customer_type] || "bg-gray-50 text-gray-600 border-gray-200"
                            }`}>
                            {customer.customer_type}
                        </span>
                    )}
                </div>
                <p className="text-[11px] text-gray-400 truncate mt-0.5">
                    {customer.email || "—"}
                </p>
            </div>
        </div>

        {/* Contact name + phone */}
        <div className="min-w-0">
            <p className="text-sm text-gray-700 leading-snug truncate">
                {customer.contact_name || customer.name}
            </p>
            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                {formatAustralianPhone(customer.phone) || "—"}
            </p>
        </div>

        {/* Jobs */}
        <div data-label="Jobs">
            {customer.job_count > 0 ? (
                <span className="text-sm font-semibold text-zinc-700">{customer.job_count}</span>
            ) : (
                <span className="text-sm text-gray-300">0</span>
            )}
        </div>

        {/* Total outstanding */}
        <div data-label="Outstanding">
            <span className="text-sm text-gray-400">—</span>
        </div>

        {/* Created at */}
        <div data-label="Created">
            <span className="text-[12px] text-gray-500">{formatDate(customer.created_at)}</span>
        </div>

        {/* Arrow */}
        <div className="flex justify-end">
            <FiChevronRight
                size={14}
                className="text-gray-200 group-hover:text-gray-400 transition-colors"
            />
        </div>
    </div>
));

// ─── Main component ───────────────────────────────────────────────────────────
const Customers = () => {
    const { company, userProfile } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();

    // ── data ──
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });
    const [stats, setStats] = useState({ total: 0, newThisMonth: 0, withContact: 0 });

    // ── filters ──
    const [searchQuery, setSearchQuery] = useState(() => load(STORAGE_KEYS.searchQuery, ""));
    const [debouncedQuery, setDebouncedQuery] = useState(() => load(STORAGE_KEYS.searchQuery, ""));
    const [sortBy, setSortBy] = useState(() => load(STORAGE_KEYS.sortBy, "newest"));
    const [showSort, setShowSort] = useState(false);
    const [showNewCustomer, setShowNewCustomer] = useState(false);

    const [customerTypeFilter, setCustomerTypeFilter] = useState(() =>
        load(STORAGE_KEYS.customerTypeFilter, "all")
    );
    const [showCustomerType, setShowCustomerType] = useState(false);
    const typeDropRef = useRef(null);

    // ── refs ──
    const listRef = useRef(null);
    const debounceRef = useRef(null);
    const sortDropRef = useRef(null);

    // ── debounce search ──
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchQuery]);

    useEffect(() => {
        save(STORAGE_KEYS.searchQuery, searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        save(STORAGE_KEYS.customerTypeFilter, customerTypeFilter);
    }, [customerTypeFilter]);

    // ── close sort dropdown on outside click ──
    useEffect(() => {
        const h = (e) => { if (!sortDropRef.current?.contains(e.target)) setShowSort(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    useEffect(() => {
        const h = (e) => {
            if (!typeDropRef.current?.contains(e.target)) setShowCustomerType(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    // ── filters builder ──
    const buildFilters = useCallback(() => ({
        searchQuery: debouncedQuery,
        sortBy,
        customerTypeFilter,
    }), [debouncedQuery, sortBy, customerTypeFilter]);

    // ── fetch stats ──
    const fetchStats = useCallback(async () => {
        if (!company?.id) return;
        try { setStats(await getCustomerStats(company.id)); }
        catch (err) { console.error("Stats error:", err); }
    }, [company?.id]);

    // ── fetch list ──
    const fetchData = useCallback(async () => {
        if (!company?.id) return;
        setIsLoading(true);
        try {
            const result = await getCustomersList(company.id, 1, PAGE_LIMIT, buildFilters());
            setCustomers(result.data);
            setPagination(result.pagination);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setIsLoading(false);
        }
    }, [company?.id, buildFilters]);

    const loadMore = useCallback(async () => {
        if (!company?.id || isLoadingMore || !pagination.hasMore) return;
        setIsLoadingMore(true);
        try {
            const result = await getCustomersList(company.id, pagination.page + 1, PAGE_LIMIT, buildFilters());
            setCustomers(prev => [...prev, ...result.data]);
            setPagination(result.pagination);
        } catch (err) {
            console.error("LoadMore error:", err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [company?.id, isLoadingMore, pagination, buildFilters]);

    // ── on mount / filter change ──
    useEffect(() => { fetchStats(); }, [company?.id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, [company?.id, debouncedQuery, sortBy, customerTypeFilter]);

    // ── realtime ──
    useEffect(() => {
        if (!company?.id) return;
        const channel = supabase
            .channel(`customers-list-${company.id}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'customers', filter: `company_id=eq.${company.id}` },
                (payload) => {
                    if (payload.eventType === 'INSERT') {
                        setCustomers(prev => [payload.new, ...prev]);
                        setStats(s => ({ ...s, total: s.total + 1, newThisMonth: s.newThisMonth + 1 }));
                        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
                    } else if (payload.eventType === 'UPDATE') {
                        setCustomers(prev => prev.map(c => c.id === payload.new.id ? { ...c, ...payload.new } : c));
                    } else if (payload.eventType === 'DELETE') {
                        setCustomers(prev => prev.filter(c => c.id !== payload.old.id));
                        setStats(s => ({ ...s, total: Math.max(0, s.total - 1) }));
                        setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
                    }
                }
            )
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [company?.id]);

    // ── infinite scroll ──
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const handler = () => {
            if (isLoadingMore || !pagination.hasMore) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) loadMore();
        };
        el.addEventListener("scroll", handler);
        return () => el.removeEventListener("scroll", handler);
    }, [pagination.hasMore, isLoadingMore, loadMore]);

    // ── virtualizer (padding-spacer pattern) ──
    const rowVirtualizer = useVirtualizer({
        count: customers.length,
        getScrollElement: () => listRef.current,
        estimateSize: () => 65,
        overscan: 8,
    });

    const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
    const SortIcon = currentSort.icon;

    return (
        <>
            <div className="customers-page flex flex-col gap-4 h-full min-h-0">

                {/* Header */}
                <div className="mb-4">
                    {/* ── Back ─────────────────────────────────────────────────────── */}
                    <BackButton
                        text="Go back"
                        variant="ghost"
                        size="sm"
                        iconType="chevron"
                        className="text-blue-600 hover:text-blue-800 mb-2"
                    />
                    <h1 className="text-2xl font-bold text-zinc-700">Customers</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Manage customers and track assignments
                    </p>
                </div>

                {/* ── Stat cards ───────────────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-3 flex-shrink-0">
                    <StatCard
                        label="Total Customers"
                        value={stats.total.toLocaleString()}
                        Icon={Users}
                    />
                    <StatCard
                        label="New This Month"
                        value={stats.newThisMonth.toLocaleString()}
                        sub="Last 30 days"
                        Icon={UserPlus}
                    />
                    <StatCard
                        label="With Contact Info"
                        value={stats.withContact.toLocaleString()}
                        sub="Have phone or email"
                        Icon={Phone}
                    />
                </div>

                {/* ── Table card ───────────────────────────────────────────────── */}
                <div className="bg-white rounded-xl border border-gray-300 shadow-md flex flex-col min-h-0 flex-shrink-0">

                    {/* Table header row */}
                    <div className="customer-toolbar flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                        <div>
                            <h1 className="text-[15px] font-bold text-zinc-700 leading-tight">Customers List</h1>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                                Total {pagination.total.toLocaleString()}
                                {pagination.hasMore && (
                                    <span className="ml-1 text-gray-300">· scroll for more</span>
                                )}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Search */}
                            <div className="relative">
                                <FiSearch
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                                    size={13}
                                />
                                <input
                                    type="text"
                                    placeholder="Search for customer..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="pl-8 pr-7 py-2 text-sm border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-600 w-96"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <FiX size={12} />
                                    </button>
                                )}
                            </div>

                            {/* Sort */}
                            <div className="relative" ref={sortDropRef}>
                                <button
                                    onClick={() => setShowSort(p => !p)}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white text-gray-600 hover:border-gray-300 hover:text-zinc-700 transition-all"
                                >
                                    <SortIcon className="w-3.5 h-3.5 shrink-0" />
                                    <span className="hidden sm:inline">{currentSort.label}</span>
                                    <FiChevronDown
                                        size={12}
                                        className={`transition-transform ${showSort ? "rotate-180" : ""}`}
                                    />
                                </button>
                                {showSort && (
                                    <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                        {SORT_OPTIONS.map(opt => {
                                            const Icon = opt.icon;
                                            const active = sortBy === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                                                    className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors
                                                    ${active ? "bg-zinc-700 text-white font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                                                >
                                                    <Icon className="w-3.5 h-3.5 shrink-0" />
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="relative" ref={typeDropRef}>
                                <button
                                    onClick={() => setShowCustomerType(p => !p)}
                                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-all ${customerTypeFilter !== "all"
                                        ? "bg-zinc-700 text-white border-zinc-700"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-zinc-700"
                                        }`}
                                >
                                    <span className="customer-type-desktop">{customerTypeFilter ? `Customer Type: ${customerTypeFilter.toUpperCase()}` : "Customer Type"}</span><span className="customer-type-mobile hidden">{customerTypeFilter === "all" ? "All types" : customerTypeFilter}</span>
                                    <FiChevronDown
                                        size={12}
                                        className={`transition-transform ${showCustomerType ? "rotate-180" : ""}`}
                                    />
                                </button>

                                {showCustomerType && (
                                    <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                        {CUSTOMER_TYPE_OPTIONS.map(opt => {
                                            const active = customerTypeFilter === opt.value;

                                            return (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => {
                                                        setCustomerTypeFilter(opt.value);
                                                        setShowCustomerType(false);
                                                    }}
                                                    className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-sm transition-colors ${active
                                                        ? "bg-zinc-700 text-white font-semibold"
                                                        : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                >
                                                    <span>{opt.label}</span>
                                                    {active && <span className="text-xs">✓</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* New Customer */}
                            {hasPermission(userProfile?.role, ['super_admin', 'admin']) &&
                                <button
                                    aria-label="New customer"
                                    onClick={() => setShowNewCustomer(true)}
                                    className="customer-add-button flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-zinc-700 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    <FiPlus size={14} />
                                    <span>New Customer</span>
                                </button>
                            }
                            <button
                                onClick={() => { fetchData(); fetchStats(); }}
                                className="customer-refresh-button p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                title="Refresh"
                            >
                                <FiRefreshCw size={13} />
                            </button>
                        </div>
                    </div>

                    {/* Column headers */}
                    <div
                        className="customer-columns grid items-center gap-4 px-5 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0"
                        style={{ gridTemplateColumns: COL_TEMPLATE }}
                    >
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Customer Name</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Contact</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Jobs</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Total Outstanding</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Created At</span>
                        <div />
                    </div>

                    {/* Scrollable body */}
                    <div
                        ref={listRef}
                        className="customer-list-scroll overflow-x-hidden overflow-y-scroll"
                        style={{ height: '60vh' }}
                    >
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center h-48 gap-3">
                                <div className="w-6 h-6 border-2 border-zinc-700 border-t-transparent rounded-full animate-spin" />
                                <p className="text-sm text-gray-500">Loading customers…</p>
                            </div>
                        ) : customers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-48 text-center px-4">
                                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                    <Users size={18} className="text-gray-400" />
                                </div>
                                <p className="text-sm font-semibold text-gray-800 mb-1">No customers found</p>
                                <p className="text-xs text-gray-400">
                                    {searchQuery ? "Try a different search term" : "Add your first customer to get started"}
                                </p>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="mt-3 text-xs font-semibold text-zinc-700 underline"
                                    >
                                        Clear search
                                    </button>
                                )}
                            </div>
                        ) : (() => {
                            const virtualItems = rowVirtualizer.getVirtualItems();
                            const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
                            const paddingBottom = virtualItems.length > 0
                                ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
                                : 0;
                            return (
                                <>
                                    {paddingTop > 0 && <div style={{ height: `${paddingTop}px` }} />}
                                    {virtualItems.map(vi => (
                                        <CustomerRow
                                            key={vi.key}
                                            virtualIndex={vi.index}
                                            measureElement={rowVirtualizer.measureElement}
                                            customer={customers[vi.index]}
                                            companySlug={companySlug}
                                            navigate={navigate}
                                            location={location}
                                        />
                                    ))}
                                    {paddingBottom > 0 && <div style={{ height: `${paddingBottom}px` }} />}
                                </>
                            );
                        })()}

                        {isLoadingMore && (
                            <div className="flex items-center justify-center py-5 gap-2">
                                <div className="w-4 h-4 border-2 border-zinc-700 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-gray-500">Loading more…</span>
                            </div>
                        )}
                        {!isLoading && !isLoadingMore && !pagination.hasMore && customers.length > 0 && (
                            <p className="text-center text-[11px] text-gray-400 py-5">
                                All {pagination.total.toLocaleString()} customers loaded
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* New Customer modal */}
            {showNewCustomer && (
                <NewCustomerModal
                    onClose={() => setShowNewCustomer(false)}
                    onCreated={(customer) => {
                        setCustomers(prev => [customer, ...prev]);
                        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
                        setStats(s => ({ ...s, total: s.total + 1, newThisMonth: s.newThisMonth + 1 }));
                    }}
                />
            )}
        </>
    );
};

export default Customers;
